import { Link, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import { AutoGlowBrand } from "./components/AutoGlowLogo";
import GlobalHeader from "../components/layout/GlobalHeader";
import { t, getLocale, setLocale } from "../lib/i18n";
import {
  Sparkle, Lightning, ChartLineUp, ShieldCheck, ArrowRight, CheckCircle,
  CarProfile, Receipt, Storefront, Users, MapPin, MagnifyingGlass, Heart, GlobeHemisphereWest,
  Wrench, PaintBrush,
} from "@phosphor-icons/react";

const CLIENT_BENEFITS = [
  { icon: MagnifyingGlass, text: "Encuentra los mejores profesionales del motor cerca de ti" },
  { icon: Lightning, text: "Reserva en segundos desde tu móvil, sin llamadas" },
  { icon: Users, text: "Detailing, mecánica general, chapa y pintura, restauración" },
  { icon: Sparkle, text: "Precio cerrado · añade complementos y gestiona tus citas" },
];

const PRO_BENEFITS = [
  { icon: ChartLineUp, text: "Agenda inteligente con control de horarios y disponibilidad" },
  { icon: Users, text: "Gestión de equipo · empleados, colaboradores y reparto de comisión configurable" },
  { icon: Receipt, text: "Hucha de impuestos · VAT/IVA/IRPF · Modelos AEAT automatizados" },
  { icon: Storefront, text: "Tu mini-web propia con marketplace global y reservas online" },
];

export default function Landing() {
  const navigate = useNavigate();
  const [locale, setLoc] = useState(getLocale());
  useEffect(() => { setLocale(locale); }, [locale]);
  const tr = (k) => t(k, locale);
  return (
    <div className="min-h-screen ag-radial text-[var(--ag-text)] overflow-x-hidden flex flex-col">
      <GlobalHeader variant="auto" testId="landing-header" />

      {/* HERO — eslogan multisectorial i18n */}
      <section className="ag-grid relative landscape:py-2">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-20 lg:py-24 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-[var(--ag-gold)]/30 rounded-full text-[10px] uppercase tracking-[0.25em] text-[var(--ag-gold)]">
            <GlobeHemisphereWest size={12} weight="fill" /> {tr("hero.tagline.eyebrow")}
          </div>
          <h1 className="font-display font-black text-3xl sm:text-5xl lg:text-6xl tracking-tighter mt-6 leading-[1.1] break-words"
              data-testid="landing-hero-headline">
            {tr("hero.tagline")}
          </h1>
          <p className="text-base sm:text-lg text-[var(--ag-text-dim)] mt-6 max-w-2xl mx-auto">
            {tr("hero.subtitle")}
          </p>

          {/* Sectores que cubrimos */}
          <div className="flex items-center justify-center gap-4 sm:gap-6 mt-8 flex-wrap">
            {[
              { icon: Sparkle, label: "Detailing" },
              { icon: Wrench, label: "Mecánica" },
              { icon: PaintBrush, label: "Chapa & Pintura" },
              { icon: CarProfile, label: "Restauración" },
            ].map((s)=>(
              <div key={s.label} className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-[var(--ag-text-dim)]">
                <s.icon size={13} weight="duotone" className="text-[var(--ag-gold)]" /> {s.label}
              </div>
            ))}
          </div>

          {/* CTA principal único · oro + secundario ghost */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-10">
            <button
              onClick={()=>navigate("/register")}
              className="group relative inline-flex items-center gap-2 bg-[var(--ag-gold)] text-[var(--ag-midnight)] font-semibold rounded-md px-6 sm:px-7 h-12 sm:h-14 text-base hover:shadow-[0_18px_46px_-14px_rgba(255,183,3,0.55)] hover:bg-[#FFCD3C] transition-all active:scale-[0.98]"
              data-testid="cta-pro">
              {tr("hero.cta.start")} · {tr("hero.trial")}
              <ArrowRight weight="bold" className="group-hover:translate-x-0.5 transition-transform" />
            </button>
            <button
              onClick={()=>navigate("/marketplace")}
              className="inline-flex items-center gap-2 text-sm font-medium text-[var(--ag-text)] hover:text-[var(--ag-gold)] transition-colors px-3 h-12"
              data-testid="cta-client">
              <MagnifyingGlass size={16} weight="duotone" /> Ver el marketplace
            </button>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5 mt-4 text-[11px] uppercase tracking-[0.15em] text-[var(--ag-text-dim)]">
            <span>✓ {tr("hero.trial")}</span>
            <span className="hidden sm:inline">·</span>
            <span>✓ Sin permanencia</span>
            <span className="hidden sm:inline">·</span>
            <span>✓ Cancela cuando quieras</span>
          </div>

          <div className="text-center mt-8">
            <button
              onClick={()=>navigate("/pricing")}
              className="text-xs uppercase tracking-[0.2em] text-[var(--ag-text-dim)] hover:text-[var(--ag-gold)] transition-colors underline-offset-4 hover:underline"
              data-testid="landing-pricing-link"
            >
              Ver planes y precios →
            </button>
          </div>

          <div className="flex items-center justify-center gap-6 mt-10 text-[10px] uppercase tracking-[0.25em] text-[var(--ag-text-dim)]">
            <span className="flex items-center gap-1.5"><Sparkle size={12} weight="fill" className="text-[var(--ag-gold)]" /> Diseñado para autónomos del motor</span>
            <span className="hidden sm:flex items-center gap-1.5"><ShieldCheck size={12} weight="fill" className="text-[var(--ag-gold)]" /> RGPD · Stripe certificado</span>
            <span className="hidden sm:flex items-center gap-1.5"><Heart size={12} weight="fill" className="text-[var(--ag-gold)]" /> Hecho en España</span>
          </div>
        </div>
      </section>

      {/* Section: WHAT WE OFFER */}
      <section className="border-t border-[var(--ag-border)] bg-[var(--ag-midnight-soft)]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
          <div className="text-center max-w-2xl mx-auto">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[var(--ag-gold)]">Qué ofrecemos</div>
            <h2 className="font-display font-black text-3xl sm:text-4xl mt-3 tracking-tighter">
              Una plataforma. <span className="ag-glow-text">Dos mundos conectados.</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mt-14">
            {/* CLIENT card */}
            <div className="ag-card p-8 border-[var(--ag-gold)]/20">
              <div className="inline-flex items-center gap-2 px-2.5 py-1 bg-[var(--ag-gold)]/10 border border-[var(--ag-gold)]/30 rounded text-[10px] uppercase tracking-wider text-[var(--ag-gold)]">
                <Heart size={11} weight="fill" /> Para clientes
              </div>
              <h3 className="font-display font-bold text-2xl mt-4 tracking-tighter">Encuentra y reserva,<br/>sin complicaciones.</h3>
              <ul className="mt-6 space-y-3">
                {CLIENT_BENEFITS.map((b) => (
                  <li key={b.text} className="flex items-start gap-3 text-sm text-[var(--ag-text)]">
                    <b.icon size={18} weight="duotone" className="text-[var(--ag-gold)] shrink-0 mt-0.5" />
                    <span>{b.text}</span>
                  </li>
                ))}
              </ul>
              <Button onClick={()=>navigate("/marketplace")} variant="outline" className="mt-7 border-[var(--ag-gold)]/40 text-[var(--ag-gold)] hover:bg-[var(--ag-gold)]/10 bg-transparent" data-testid="offer-client-cta">
                Ver centros disponibles <ArrowRight className="ml-2" weight="bold" />
              </Button>
            </div>

            {/* PRO card */}
            <div className="ag-card p-8 border-[var(--ag-aqua)]/20">
              <div className="inline-flex items-center gap-2 px-2.5 py-1 bg-[var(--ag-aqua)]/15 border border-[var(--ag-aqua)]/30 rounded text-[10px] uppercase tracking-wider text-[var(--ag-aqua-soft)]">
                <Storefront size={11} weight="fill" /> Para autónomos, microempresas y empresas
              </div>
              <h3 className="font-display font-bold text-2xl mt-4 tracking-tighter">El software definitivo<br/>para optimizar tu negocio.</h3>
              <ul className="mt-6 space-y-3">
                {PRO_BENEFITS.map((b) => (
                  <li key={b.text} className="flex items-start gap-3 text-sm text-[var(--ag-text)]">
                    <b.icon size={18} weight="duotone" className="text-[var(--ag-aqua-soft)] shrink-0 mt-0.5" />
                    <span>{b.text}</span>
                  </li>
                ))}
              </ul>
              <Button onClick={()=>navigate("/register")} className="ag-btn-aqua mt-7 h-10" data-testid="offer-pro-cta">
                Probar AutoGlow gratis · 30 días <ArrowRight className="ml-2" weight="bold" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Section: STORY */}
      <section className="border-t border-[var(--ag-border)]">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 py-20 sm:py-24">
          <div className="text-center">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[var(--ag-gold)]">Nuestra historia</div>
            <h2 className="font-display font-black text-3xl sm:text-4xl mt-3 tracking-tighter">
              Por qué nació <span className="ag-glow-text">AutoGlow</span>.
            </h2>
          </div>
          <div className="mt-10 space-y-5 text-base sm:text-lg text-[var(--ag-text-dim)] leading-relaxed text-center max-w-3xl mx-auto">
            <p>
              AutoGlow nació para resolver una necesidad real en el sector del cuidado del automóvil:
              <span className="text-[var(--ag-text)] font-semibold"> la falta de herramientas tecnológicas sencillas que unan la gestión interna del taller con el cliente final.</span>
            </p>
            <p>
              Creamos esta plataforma para que los <b className="text-[var(--ag-text)]">profesionales del motor,
              detailing, mecánica general, chapa y pintura, y restauración</b> puedan
              <b className="text-[var(--ag-text)]"> automatizar su contabilidad, coordinar turnos y gestionar colaboraciones</b> de forma justa, transparente y 100% legal,
              mientras los clientes disfrutan de una <b className="text-[var(--ag-gold)]">experiencia de reserva premium, rápida y sin fricciones.</b>
            </p>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-3 mt-10">
            <Button onClick={()=>navigate("/marketplace")} variant="outline" className="border-[var(--ag-border)] bg-transparent text-[var(--ag-text)] hover:bg-[var(--ag-midnight-soft)]">
              <MagnifyingGlass className="mr-2" /> Soy cliente
            </Button>
            <Button onClick={()=>navigate("/register")} className="ag-btn-gold h-10 px-6">
              <Storefront className="mr-2" /> Soy profesional
            </Button>
          </div>
        </div>
      </section>

      <footer className="border-t border-[var(--ag-border)] bg-[var(--ag-midnight)] text-[var(--ag-text)]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-8 flex flex-wrap items-center justify-between gap-4 text-xs text-[var(--ag-text-dim)]">
          <div className="flex items-center gap-2">
            <AutoGlowBrand size={14} autoColor="#FFFFFF" />
            <span>© {new Date().getFullYear()} · Marketplace global de detailing & gestión profesional</span>
          </div>
          <div className="flex gap-5">
            <Link to="/marketplace" className="hover:text-white">Marketplace</Link>
            <Link to="/login" className="hover:text-white">Acceso</Link>
            <Link to="/register" className="hover:text-white">Empieza gratis</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
